import { useCallback, useContext, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { ShipmentsGrid } from '@/components/common/shipments-grid';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { EMBRYO_STATUS } from '@/pages/fresh-straw/constants';
import { ShipmentStatus } from '@/pages/shipping/utils/enums';
import { PrimaryContext } from '@/services';
import { useAccessToken } from '@/services/auth';
import {
  AddEmbryosToShipmentInput,
  EmbryoDto,
  embryoServiceApi,
} from '@/services/embryo-service';
import { ShipmentDto } from '@/services/semen-service';
import { shipmentEmbryosOptions } from '@/services/shipping';
import { useContextMenu } from '@/utils/context-menu';

import { useEmbryoAppContext } from '../../context/EmbryoAppContext';
import {
  embryoAppShipmentSideBar,
  shipmentTabEmbryoColumnDefs,
} from '../../util/columns/shipments';
import { EmbryoShipmentDetailsPanel } from './EmbryoShipmentDetailsPanel';
import { EmbryoShipmentOverrideDialog } from './EmbryoShipmentOverrideDialog';
import { useAddEmbryosToShipment } from './useAddEmbryosToShipment';

const LOCKED_SHIPMENT_STATUSES = [
  ShipmentStatus.IN_PROGRESS,
  ShipmentStatus.TRANSFER_TRIP,
  ShipmentStatus.SHIPPED,
];

type ShippingIntegrationLocationState = {
  shippingEmbryos?: EmbryoDto[];
};

export const ShippingIntegrationTab = () => {
  const token = useAccessToken();
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const { themeMode } = useContext(PrimaryContext);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isSelectedEmbryosShippingEnabled = useFeatureFlag(
    'Selected_Embryos_Shipping_Integration',
  );
  const isEmbryoRemoveFromShipmentEnabled = useFeatureFlag(
    'Embryo_Remove_From_Shipment',
  );
  const location = useLocation() as {
    state?: ShippingIntegrationLocationState;
  };
  const context = useEmbryoAppContext();
  const { setLoading } = context;

  const embryoGridRowData = isSelectedEmbryosShippingEnabled
    ? (location.state?.shippingEmbryos ?? [])
    : (context.selectedEmbryos ?? []);

  const { getContextMenuItems: embryoContextMenu } = useContextMenu({
    idKey: 'id',
  });
  const shipmentsGridRef = useRef<AgGridReact>(null);

  const embryoGridRef = useRef<AgGridReact>(null);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentDto | null>(
    null,
  );
  const [selectedEmbryos, setSelectedEmbryos] = useState<EmbryoDto[]>([]);
  const [shipmentSearchQuery, setShipmentSearchQuery] = useState<string>('');
  const [shipmentSearchInput, setShipmentSearchInput] = useState<string>('');

  const handleShipmentSearch = () => {
    setShipmentSearchQuery(shipmentSearchInput);
  };

  const refreshEmbryoGrid = useCallback(() => {
    embryoGridRef.current?.api?.refreshCells({ force: true });
  }, []);

  const handleEmbryoGridReady = useCallback(() => {
    refreshEmbryoGrid();
  }, [refreshEmbryoGrid]);

  const onEmbryoSelectionChanged = useCallback(() => {
    const selectedRows = embryoGridRef.current?.api?.getSelectedRows() as
      | EmbryoDto[]
      | undefined;
    if (selectedRows && selectedRows.length > 0) {
      setSelectedEmbryos(selectedRows);
    } else {
      setSelectedEmbryos([]);
    }
  }, []);

  const handleAddToShipment = async () => {
    if (!selectedShipment || selectedEmbryos.length === 0) {
      return;
    }

    if (overrideFlow.isOverrideEnabled) {
      await overrideFlow.addEmbryosToShipmentV2(
        selectedShipment.id,
        selectedEmbryos,
      );
      return;
    }

    setLoading(true);
    try {
      const embryoList = selectedEmbryos.flatMap((embryo) =>
        embryo.id ? [{ embryoId: embryo.id }] : [],
      );
      if (
        !selectedShipment.id ||
        embryoList.length !== selectedEmbryos.length
      ) {
        return;
      }

      const input: AddEmbryosToShipmentInput = {
        shipmentId: selectedShipment.id,
        embryoList,
      };

      const result = await embryoServiceApi.addEmbryosToShipment(input);

      const success = result.success ?? false;
      const message = result.message ?? 'Operation completed';

      const isWarning = message.toLowerCase().includes('warning');
      const severity = isWarning ? 'warn' : success ? 'success' : 'error';
      const summary = isWarning
        ? t('common.warning')
        : success
          ? t('common.success')
          : t('common.error');
      const life = isWarning || !success ? ToastLife.LONG : ToastLife.DEFAULT;

      dispatch(
        addToast({
          severity,
          summary,
          detail: message,
          life,
        }),
      );

      if (success) {
        handleClearSelection();
        if (isEmbryoRemoveFromShipmentEnabled && selectedShipment?.id) {
          queryClient.invalidateQueries({
            queryKey: shipmentEmbryosOptions(token, selectedShipment.id)
              .queryKey,
          });
        }
      }
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('common.error'),
          detail:
            error instanceof Error
              ? error.message
              : t('shipping.failedToAddEmbryos'),
          life: ToastLife.DEFAULT,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedEmbryos([]);
    if (!isSelectedEmbryosShippingEnabled) {
      context.setSelectedEmbryos([]);
    }
    embryoGridRef.current?.api?.deselectAll();
    embryoGridRef.current?.api?.refreshCells({ force: true });
  };

  const isShipmentLocked = selectedShipment?.shipmentStatus
    ? LOCKED_SHIPMENT_STATUSES.includes(selectedShipment.shipmentStatus)
    : false;

  const hasInvalidEmbryos =
    selectedEmbryos.length > 0 &&
    selectedEmbryos.some((embryo) => embryo.status !== EMBRYO_STATUS.FROZEN);

  const isAddDisabled =
    !selectedEmbryos.length ||
    !selectedShipment?.id ||
    isShipmentLocked ||
    hasInvalidEmbryos;

  const handleShipmentUpdated = useCallback(() => {
    shipmentsGridRef.current?.api?.refreshServerSide({ purge: true });
  }, []);

  const handleAddSuccess = () => {
    handleClearSelection();
    handleShipmentUpdated();
  };

  const overrideFlow = useAddEmbryosToShipment({
    setLoading,
    onSuccess: handleAddSuccess,
  });

  if (isEmbryoRemoveFromShipmentEnabled) {
    return (
      <ConfirmProvider>
        <div className="flex h-full min-h-0 flex-col p-4">
          <div className="grid min-h-0 grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="space-y-6 xl:col-span-2">
              <div className="flex min-h-0 flex-col">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('shipping.selectedEmbryosForShipping')}
                  </h3>
                  <div className="flex items-center justify-end gap-4">
                    <Button
                      label={t('common.clear')}
                      icon="pi pi-times"
                      size="small"
                      text
                      onClick={handleClearSelection}
                    />
                    <Tooltip
                      target=".add-to-shipment-btn"
                      position="left"
                      content={
                        isShipmentLocked
                          ? t('shipping.cannotAddToLockedShipment')
                          : hasInvalidEmbryos
                            ? t('shipping.onlyFrozenEmbryosAllowed')
                            : t('shipping.addToShipment')
                      }
                    />
                    <span className="add-to-shipment-btn">
                      <Button
                        label={t('shipping.addToShipment')}
                        icon="pi pi-arrow-right"
                        size="small"
                        disabled={isAddDisabled}
                        onClick={handleAddToShipment}
                      />
                    </span>
                  </div>
                </div>
                <div className="h-[250px] min-h-0">
                  <WithLocalStorage storageKey="shipping-embryo-grid">
                    <AgGridReact
                      gridOptions={defaultGridOptions}
                      ref={embryoGridRef}
                      className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full`}
                      columnDefs={shipmentTabEmbryoColumnDefs}
                      rowData={embryoGridRowData}
                      rowSelection="multiple"
                      suppressRowClickSelection={true}
                      defaultColDef={defaultColDef}
                      sideBar={embryoAppShipmentSideBar}
                      getContextMenuItems={embryoContextMenu}
                      getRowId={(params) => String(params.data.id)}
                      onSelectionChanged={onEmbryoSelectionChanged}
                      onGridReady={handleEmbryoGridReady}
                      onFilterChanged={refreshEmbryoGrid}
                      onSortChanged={refreshEmbryoGrid}
                      rowModelType="clientSide"
                    />
                  </WithLocalStorage>
                </div>
              </div>

              <ShipmentsGrid
                gridRef={shipmentsGridRef}
                selectedShipment={selectedShipment}
                setSelectedShipment={setSelectedShipment}
                filters={
                  shipmentSearchQuery ? { search: shipmentSearchQuery } : {}
                }
                storageKey="shipping-shipments-grid"
                title={t('shipping.strawShipments')}
                showSearch={true}
                searchInput={shipmentSearchInput}
                onSearchInputChange={setShipmentSearchInput}
                onSearch={handleShipmentSearch}
              />
            </div>

            <div className="xl:col-span-1">
              <EmbryoShipmentDetailsPanel
                selectedShipment={selectedShipment}
                isShipmentLocked={isShipmentLocked}
                onShipmentUpdated={handleShipmentUpdated}
              />
            </div>
          </div>
        </div>
        {overrideFlow.isOverrideEnabled ? (
          <EmbryoShipmentOverrideDialog
            visible={overrideFlow.isOverrideDialogVisible}
            conflicts={overrideFlow.conflicts}
            isSubmitting={overrideFlow.isSubmitting}
            onConfirm={overrideFlow.confirmOverride}
            onCancel={overrideFlow.cancelOverride}
          />
        ) : null}
      </ConfirmProvider>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col p-4">
      <div className="grid min-h-0 grid-cols-1 gap-6">
        <div className="space-y-6">
          <div className="flex min-h-0 flex-col">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('shipping.selectedEmbryosForShipping')}
              </h3>
              <div className="flex items-center justify-end gap-4">
                <Button
                  label={t('common.clear')}
                  icon="pi pi-times"
                  size="small"
                  text
                  onClick={handleClearSelection}
                />
                <Tooltip
                  target=".add-to-shipment-btn"
                  position="left"
                  content={
                    isShipmentLocked
                      ? t('shipping.cannotAddToLockedShipment')
                      : hasInvalidEmbryos
                        ? t('shipping.onlyFrozenEmbryosAllowed')
                        : t('shipping.addToShipment')
                  }
                />
                <span className="add-to-shipment-btn">
                  <Button
                    label={t('shipping.addToShipment')}
                    icon="pi pi-arrow-right"
                    size="small"
                    disabled={isAddDisabled}
                    onClick={handleAddToShipment}
                  />
                </span>
              </div>
            </div>
            <div className="h-[250px] min-h-0">
              <WithLocalStorage storageKey="shipping-embryo-grid">
                <AgGridReact
                  gridOptions={defaultGridOptions}
                  ref={embryoGridRef}
                  className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-full`}
                  columnDefs={shipmentTabEmbryoColumnDefs}
                  rowData={embryoGridRowData}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  defaultColDef={defaultColDef}
                  sideBar={embryoAppShipmentSideBar}
                  getContextMenuItems={embryoContextMenu}
                  getRowId={(params) => String(params.data.id)}
                  onSelectionChanged={onEmbryoSelectionChanged}
                  onGridReady={handleEmbryoGridReady}
                  onFilterChanged={refreshEmbryoGrid}
                  onSortChanged={refreshEmbryoGrid}
                  rowModelType="clientSide"
                />
              </WithLocalStorage>
            </div>
          </div>

          <ShipmentsGrid
            gridRef={shipmentsGridRef}
            selectedShipment={selectedShipment}
            setSelectedShipment={setSelectedShipment}
            filters={shipmentSearchQuery ? { search: shipmentSearchQuery } : {}}
            storageKey="shipping-shipments-grid"
            title={t('shipping.strawShipments')}
            showSearch={true}
            searchInput={shipmentSearchInput}
            onSearchInputChange={setShipmentSearchInput}
            onSearch={handleShipmentSearch}
          />
        </div>
      </div>
      {overrideFlow.isOverrideEnabled ? (
        <EmbryoShipmentOverrideDialog
          visible={overrideFlow.isOverrideDialogVisible}
          conflicts={overrideFlow.conflicts}
          isSubmitting={overrideFlow.isSubmitting}
          onConfirm={overrideFlow.confirmOverride}
          onCancel={overrideFlow.cancelOverride}
        />
      ) : null}
    </div>
  );
};
